import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { buildTimesheetPrintHtml, TimesheetPrintLog } from './timesheet-print.util';
import { ManualComponent } from '../manual/manual.component';

interface TimesheetLog {
  id: number;
  date: string;
  time_in: string | null;
  time_out: string | null;
  leave_type: string | null;
  is_break: boolean;
  total_hours: string;
  remarks: string | null;
}

interface TimesheetOncallEntry {
  id: number;
  date_from: string;
  date_to: string;
  time_in: string | null;
  time_out: string | null;
  hours: string;
  notes: string | null;
}

interface TimesheetApproverAction {
  id: number;
  timesheet_id: number;
  approver_id: number;
  approver_name: string;
  approver_email: string;
  action: 'approved' | 'rejected';
  reason: string | null;
  actioned_at: string;
}

interface Timesheet {
  id: number;
  employee_id: string;
  pay_period_year: number;
  pay_period_month: number;
  pay_period: string;
  total_hours: string;
  agreed_hours_weekly: number;
  status: string;
  submitted_at: string | null;
  client_approved_at: string | null;
  client_approved_by: string | null;
  client_rejected_at: string | null;
  client_rejected_by: string | null;
  client_rejection_reason: string | null;
  client_comment: string | null;
  payroll_finalized: boolean;
  // 0 = no on-call stipend this period; 1 = one on-call rotation logged;
  // 2+ = that many separate rotations, each its own stipend. Flag only —
  // no dollar amount, just staffing context for the client.
  oncall_stipend_stint_count: number;
  // No dollar fields — the actual on-call hours/dates logged this period,
  // shown to the client as staffing context regardless of whether this
  // employee is on a stipend arrangement (oncall_stipend_stint_count above
  // only ever counts stipend-eligible staff).
  oncall_entries: TimesheetOncallEntry[];
  logs: TimesheetLog[];
  approver_actions?: TimesheetApproverAction[];
}

interface Employee {
  employee_id: string;
  name: string;
  role: string;
  agreed_hours: number;
  pto_balance: number;
  timesheets: Timesheet[];
}

interface PortalData {
  approver: { first_name: string; last_name: string; title?: string; email: string };
  client_code: string;
  employees: Employee[];
  history_start?: { year: number; month: number } | null;
}

interface LeaveRequest {
  id: number;
  employee_id: string;
  leave_type: 'pto' | 'emergency';
  day_portion: 'full' | 'half';
  date_from: string;
  date_to: string;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  approved_by: string | null;
  approved_by_type: 'hr' | 'client' | null;
  rejected_by: string | null;
  rejected_by_type: 'hr' | 'client' | null;
  rejection_reason: string | null;
  created_at: string;
}

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

@Component({
  selector: 'app-viewer',
  imports: [CommonModule, FormsModule, HttpClientModule, ManualComponent],
  templateUrl: './viewer.html',
  styleUrl: './viewer.scss',
})
export class Viewer implements OnInit {
  clientCode = '';
  approverId = '';
  token = '';

  loading = true;
  invalid = false;
  data: PortalData | null = null;

  activeTab: 'timesheets' | 'leave-requests' | 'manual' = 'timesheets';

  leaveRequests: LeaveRequest[] = [];

  // Detail view modal
  viewingTimesheet: Timesheet | null = null;
  viewingEmployee: Employee | null = null;

  openView(emp: Employee, ts: Timesheet): void {
    this.viewingEmployee = emp;
    this.viewingTimesheet = ts;
  }

  closeView(): void {
    this.viewingTimesheet = null;
    this.viewingEmployee = null;
  }

  oncallDateLabel(entry: TimesheetOncallEntry): string {
    if (entry.date_from === entry.date_to) return entry.date_from;
    return `${entry.date_from} → ${entry.date_to}`;
  }

  oncallTimeLabel(entry: TimesheetOncallEntry): string {
    if (!entry.time_in && !entry.time_out) return '—';
    return `${entry.time_in ?? '—'} – ${entry.time_out ?? '—'}`;
  }

  getOncallTotalHours(ts: Timesheet): number {
    return (ts.oncall_entries ?? []).reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
  }

  leaveLabel(type: string | null): string {
    const map: Record<string, string> = {
      day_off: 'Day Off',
      pto_full: 'PTO – Full Day', pto_half: 'PTO – Half Day',
      el_full: 'Emergency Leave – Full Day', el_half: 'Emergency Leave – Half Day',
    };
    return type ? (map[type] ?? type) : 'Worked';
  }

  // Daily-log/on-call time_in/time_out are shown as plain text here, not
  // through a native time input -- format 12-hour so it reads the same way
  // the employee's own TA portal form does, instead of a raw 24-hour DB string.
  time12(t: string | null | undefined): string {
    if (!t) return '—';
    const [hStr, mStr] = t.split(':');
    let h = parseInt(hStr, 10);
    if (isNaN(h)) return '—';
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mStr} ${period}`;
  }

  // How many hours of this row's own raw time_in-time_out span never made
  // it into its stored total_hours -- e.g. a logged Break row that
  // overlaps this Shift row's time range (confirmed 2026-10-19) gets its
  // overlap deducted by the backend, but a row otherwise identical to its
  // raw span shows no deduction. No overlap logic is duplicated here: the
  // backend's total_hours is already the final, correct, post-deduction
  // figure, so the gap between it and the row's own raw span is exactly
  // what was deducted, whatever the reason (this also surfaces the older
  // static-schedule-window deduction, not just the new overlap case).
  breakDeductedHours(log: TimesheetLog): number {
    if (log.is_break || !log.time_in || !log.time_out) return 0;

    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const inMin = toMinutes(log.time_in);
    let outMin  = toMinutes(log.time_out);
    if (outMin <= inMin) outMin += 1440;
    const rawMinutes = outMin - inMin;

    const deductedHours = Math.round(((rawMinutes - (+log.total_hours) * 60) / 60) * 100) / 100;
    return deductedHours > 0.01 ? deductedHours : 0;
  }

  statusLabel(ts: Timesheet): string {
    if (ts.status === 'client_approved') {
      return ts.client_approved_by === 'Auto Approved' ? 'Auto-Approved' : 'Approved';
    }
    const map: Record<string, string> = {
      draft:              'Draft',
      submitted:          'Awaiting Review',
      client_rejected:    'Flagged',
      approved:           'Approved',
      revision_requested: 'Under Review',
      expired:            'Expired',
    };
    return map[ts.status] ?? ts.status;
  }

  statusKey(ts: Timesheet): string {
    if (ts.status === 'client_approved' && ts.client_approved_by === 'Auto Approved') {
      return 'auto_approved';
    }
    return ts.status;
  }

  isAutoApproved(ts: Timesheet): boolean {
    return ts.status === 'client_approved' && ts.client_approved_by === 'Auto Approved';
  }

  get pendingTotal(): number {
    if (!this.data) return 0;
    return this.displayedEmployees.reduce(
      (sum, emp) => sum + this.filteredTimesheets(emp).filter(t => t.status === 'submitted').length, 0
    );
  }

  // Reject modal state
  rejectingTimesheet: Timesheet | null = null;
  rejectReason = '';
  rejectSubmitting = false;
  rejectError = '';

  // Employee filter
  selectedEmp: Employee | null = null;

  get displayedEmployees(): Employee[] {
    if (!this.data) return [];
    return this.selectedEmp
      ? this.data.employees.filter(e => e.employee_id === this.selectedEmp!.employee_id)
      : this.data.employees;
  }

  selectEmployee(emp: Employee | null): void {
    this.selectedEmp = emp;
  }

  pendingCount(emp: Employee): number {
    return emp.timesheets.filter(t => t.status === 'submitted').length;
  }

  // Period filter — three independent dropdowns
  selectedYear:  number | null = null;
  selectedMonth: number | null = null;
  selectedHalf:  string | null = null;

  get isPeriodFiltered(): boolean {
    return !!(this.selectedYear || this.selectedMonth || this.selectedHalf);
  }

  get yearOptions(): number[] {
    const years = new Set<number>([new Date().getFullYear()]);
    if (this.data) {
      for (const emp of this.data.employees)
        for (const ts of emp.timesheets)
          years.add(ts.pay_period_year);
    }
    return [...years].sort((a, b) => b - a);
  }

  // Hides months the backend would never return data for anyway (before
  // the configured history-start cutoff), so the dropdown doesn't offer a
  // selection that's a guaranteed dead end. Only restricts when a specific
  // year is picked and that year is exactly the cutoff year — a month with
  // no year selected could still match a later, unrestricted year.
  get monthOptions(): { value: number; label: string }[] {
    const all = MONTH_NAMES.slice(1).map((label, i) => ({ value: i + 1, label }));
    const start = this.data?.history_start;
    if (!start || !this.selectedYear || this.selectedYear > start.year) return all;
    if (this.selectedYear < start.year) return [];
    return all.filter(m => m.value >= start.month);
  }

  // A month picked under one year can fall outside monthOptions once the
  // year changes (e.g. August was valid for 2025, not for 2026's cutoff) —
  // clear it rather than leave a selected value that's no longer listed.
  onYearChange(): void {
    if (this.selectedMonth && !this.monthOptions.some(m => m.value === this.selectedMonth)) {
      this.selectedMonth = null;
    }
  }

  // Fixed, not derived from data — a period with no timesheet yet (e.g. the
  // current period, before anything's been submitted) must still be
  // selectable, not just periods that already happen to have a record.
  readonly halfOptions: { value: string; label: string }[] = [
    { value: '1-15',   label: '1 – 15' },
    { value: '16-end', label: '16 – End' },
  ];

  filteredTimesheets(emp: Employee): Timesheet[] {
    return emp.timesheets.filter(ts => {
      // Defense in depth -- the backend already excludes drafts (a TA's own
      // in-progress, unsubmitted copy), but never render one here either if
      // it somehow shows up in the payload.
      if (ts.status === 'draft') return false;
      if (this.selectedYear  && ts.pay_period_year  !== this.selectedYear)  return false;
      if (this.selectedMonth && ts.pay_period_month !== this.selectedMonth) return false;
      if (this.selectedHalf  && ts.pay_period       !== this.selectedHalf)  return false;
      return true;
    });
  }

  // Excel-style expand/collapse
  expandedTsIds = new Set<number>();

  toggleTsExpand(id: number): void {
    if (this.expandedTsIds.has(id)) this.expandedTsIds.delete(id);
    else this.expandedTsIds.add(id);
  }

  // Overtime helpers
  periodExpectedHours(ts: Timesheet): number {
    if (!ts.agreed_hours_weekly) return 0;
    const daily = ts.agreed_hours_weekly / 5;
    // Distinct weekday DATES, not log rows — a split-shift day logs more
    // than one row for the same date and would otherwise double-count it,
    // inflating expected hours and masking real overtime.
    const weekdays = new Set(
      ts.logs
        .filter(l => {
          const d = new Date(l.date + 'T00:00:00');
          return d.getDay() >= 1 && d.getDay() <= 5;
        })
        .map(l => l.date)
    ).size;
    return Math.round(daily * weekdays * 100) / 100;
  }

  periodOvertimeHours(ts: Timesheet): number {
    return Math.max(0, +ts.total_hours - this.periodExpectedHours(ts));
  }

  // Downloads a single timesheet's daily log as a PDF via the browser's own
  // print dialog — same pattern as ta-portal-ui's payslip "Save as PDF",
  // but hours-only: this portal never shows pay figures to clients.
  downloadTimesheetPdf(ts: Timesheet, emp: Employee): void {
    const expected  = this.periodExpectedHours(ts);
    const overtime  = this.periodOvertimeHours(ts);
    const logs: TimesheetPrintLog[] = ts.logs.map(log => {
      // Same deduction-visibility as the on-screen table (breakDeductedHours())
      // -- a printed/saved copy shouldn't hide why a shift reads fewer hours
      // than its raw time_in-time_out span.
      const deducted = this.breakDeductedHours(log);
      const hoursLabel = (+log.total_hours === 0) ? '—' : (+log.total_hours).toFixed(2);
      return {
        dateLabel:  new Date(log.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
        timeIn:     this.time12(log.time_in),
        timeOut:    this.time12(log.time_out),
        typeLabel:  log.is_break ? 'Break' : this.leaveLabel(log.leave_type),
        hoursLabel: deducted > 0 ? `${hoursLabel}<br><span class="small">−${deducted.toFixed(2)} hr break</span>` : hoursLabel,
        remarks:    log.remarks || '',
      };
    });

    const html = buildTimesheetPrintHtml(
      emp.name,
      emp.employee_id,
      this.periodLabel(ts),
      (+ts.total_hours).toFixed(2),
      ts.agreed_hours_weekly ? expected.toFixed(2) : null,
      overtime > 0 ? `+${overtime.toFixed(2)} hrs` : null,
      logs,
      { includePrintButton: false }
    );

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();

    iframe.contentWindow!.onload = () => {
      setTimeout(() => {
        iframe.contentWindow!.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 250);
    };
  }

  // Action feedback
  actionPending: number | null = null;

  constructor(private route: ActivatedRoute, private http: HttpClient) {}

  // Set when the link carries ?year=&month=&period= (e.g. from a bulk
  // notification email) — loadData() uses this instead of auto-detecting
  // the most recent submitted period, so the viewer lands on the exact
  // period the email was actually about.
  private requestedPeriod: { year: number; month: number; half: string | null } | null = null;

  // Set when the link carries ?employee_id= (an individual approver
  // notification, not a bulk one) — loadData() pre-selects that one
  // employee instead of leaving "All TAs" showing, so clicking the email
  // for one specific TA's timesheet lands directly on their view.
  private requestedEmployeeId: string | null = null;

  ngOnInit(): void {
    this.clientCode = this.route.snapshot.paramMap.get('clientCode') ?? '';
    this.approverId = this.route.snapshot.paramMap.get('approverId') ?? '';
    this.token      = this.route.snapshot.paramMap.get('token') ?? '';

    const qp = this.route.snapshot.queryParamMap;
    const year  = Number(qp.get('year'));
    const month = Number(qp.get('month'));
    if (year && month) {
      this.requestedPeriod = { year, month, half: qp.get('period') };
    }
    this.requestedEmployeeId = qp.get('employee_id');

    this.loadData();
  }

  private get baseUrl(): string {
    return `${environment.apiUrl}/api/partner/${this.clientCode}/${this.approverId}/${this.token}`;
  }

  // year*1000 + month*10 + halfRank — a single comparable number for
  // "which period is later," since periods aren't natively orderable.
  private periodRank(year: number, month: number, half: string): number {
    return year * 1000 + month * 10 + (half === '16-end' ? 1 : 0);
  }

  // The period that actually needs review is the one that just closed and
  // has submitted timesheets, not whatever's "in progress" today — the
  // current in-progress period usually has nothing submitted yet. Default
  // to the most recent period with at least one non-draft timesheet.
  private mostRecentSubmittedPeriod(): { year: number; month: number; half: string } | null {
    if (!this.data) return null;
    let best: { year: number; month: number; half: string } | null = null;
    let bestRank = -Infinity;
    for (const emp of this.data.employees) {
      for (const ts of emp.timesheets) {
        if (ts.status === 'draft') continue;
        const rank = this.periodRank(ts.pay_period_year, ts.pay_period_month, ts.pay_period);
        if (rank > bestRank) {
          bestRank = rank;
          best = { year: ts.pay_period_year, month: ts.pay_period_month, half: ts.pay_period };
        }
      }
    }
    return best;
  }

  // Fallback when nothing's been submitted at all yet: the previous
  // (just-closed) period relative to today, not the in-progress one.
  private previousPeriodFromToday(): { year: number; month: number; half: string } {
    const now = new Date();
    if (now.getDate() <= 15) {
      const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { year: prevMonthDate.getFullYear(), month: prevMonthDate.getMonth() + 1, half: '16-end' };
    }
    return { year: now.getFullYear(), month: now.getMonth() + 1, half: '1-15' };
  }

  loadData(): void {
    this.loading = true;
    this.http.get<PortalData>(this.baseUrl).subscribe({
      next: (res) => {
        this.data = res;
        this.loading = false;

        if (this.requestedPeriod) {
          this.selectedYear  = this.requestedPeriod.year;
          this.selectedMonth = this.requestedPeriod.month;
          this.selectedHalf  = this.requestedPeriod.half;
        } else {
          const target = this.mostRecentSubmittedPeriod() ?? this.previousPeriodFromToday();
          this.selectedYear  = target.year;
          this.selectedMonth = target.month;
          this.selectedHalf  = target.half;
        }

        if (this.requestedEmployeeId) {
          const emp = this.data.employees.find(e => e.employee_id === this.requestedEmployeeId);
          if (emp) this.selectEmployee(emp);
        }
      },
      error: ()  => { this.invalid = true; this.loading = false; },
    });

    this.http.get<LeaveRequest[]>(`${this.baseUrl}/leave-requests`).subscribe({
      next: (res) => { this.leaveRequests = res; },
      error: () => {},
    });
  }

  // ── Leave requests ──────────────────────────────────────
  employeeName(employeeId: string): string {
    return this.data?.employees.find(e => e.employee_id === employeeId)?.name ?? employeeId;
  }

  get displayedLeaveRequests(): LeaveRequest[] {
    return this.selectedEmp
      ? this.leaveRequests.filter(r => r.employee_id === this.selectedEmp!.employee_id)
      : this.leaveRequests;
  }

  get pendingLeaveTotal(): number {
    return this.displayedLeaveRequests.filter(r => r.status === 'pending').length;
  }

  pendingLeaveCount(emp: Employee): number {
    return this.leaveRequests.filter(r => r.employee_id === emp.employee_id && r.status === 'pending').length;
  }

  leaveTypeLabel(type: string): string {
    return type === 'pto' ? 'PTO' : 'Emergency Leave';
  }

  leavePortionLabel(portion: string): string {
    return portion === 'half' ? 'Half day' : 'Full day';
  }

  leaveDateRange(r: LeaveRequest): string {
    return r.date_from === r.date_to
      ? this.fmtDate(r.date_from)
      : `${this.fmtDate(r.date_from)} – ${this.fmtDate(r.date_to)}`;
  }

  leaveDays(r: LeaveRequest): number {
    const from = new Date(r.date_from + 'T00:00:00');
    const to   = new Date(r.date_to + 'T00:00:00');
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return 0;
    const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
    return r.day_portion === 'half' ? 0.5 : days;
  }

  private fmtDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  leaveActionPending: number | null = null;

  approveLeave(r: LeaveRequest): void {
    this.leaveActionPending = r.id;
    this.http.put<LeaveRequest>(
      `${this.baseUrl}/leave-requests/${r.id}/approve`, {}
    ).subscribe({
      next: (res) => { this.patchLeaveRequest(res); this.leaveActionPending = null; },
      error: () => { this.leaveActionPending = null; },
    });
  }

  // Reject modal (leave requests) — kept separate from the timesheet flag
  // modal above since the two flows target different endpoints/records.
  rejectingLeaveRequest: LeaveRequest | null = null;
  rejectLeaveReason = '';
  rejectLeaveSubmitting = false;
  rejectLeaveError = '';

  openRejectLeave(r: LeaveRequest): void {
    this.rejectingLeaveRequest = r;
    this.rejectLeaveReason = '';
    this.rejectLeaveError = '';
  }

  closeRejectLeave(): void {
    this.rejectingLeaveRequest = null;
    this.rejectLeaveReason = '';
    this.rejectLeaveError = '';
  }

  submitRejectLeave(): void {
    if (!this.rejectLeaveReason.trim()) {
      this.rejectLeaveError = 'Please describe the reason for declining.';
      return;
    }
    this.rejectLeaveSubmitting = true;
    this.rejectLeaveError = '';
    this.http.put<LeaveRequest>(
      `${this.baseUrl}/leave-requests/${this.rejectingLeaveRequest!.id}/reject`,
      { reason: this.rejectLeaveReason }
    ).subscribe({
      next: (res) => {
        this.patchLeaveRequest(res);
        this.rejectLeaveSubmitting = false;
        this.closeRejectLeave();
      },
      error: (err: HttpErrorResponse) => {
        this.rejectLeaveError = err.error?.message || 'Something went wrong. Please try again.';
        this.rejectLeaveSubmitting = false;
      },
    });
  }

  private patchLeaveRequest(updated: LeaveRequest): void {
    const idx = this.leaveRequests.findIndex(r => r.id === updated.id);
    if (idx !== -1) this.leaveRequests[idx] = updated;
  }

  periodLabel(ts: Timesheet): string {
    const month = MONTH_NAMES[ts.pay_period_month] ?? '';
    const half  = ts.pay_period === '1-15' ? '1–15' : '16–End';
    return `${month} ${half}, ${ts.pay_period_year}`;
  }

  approve(ts: Timesheet): void {
    this.actionPending = ts.id;
    this.http.put<{ success: boolean; timesheet: Timesheet }>(
      `${this.baseUrl}/timesheets/${ts.id}/approve`, {}
    ).subscribe({
      next: (res) => {
        this.patchTimesheet(res.timesheet);
        this.actionPending = null;
      },
      error: () => { this.actionPending = null; },
    });
  }

  openReject(ts: Timesheet): void {
    this.rejectingTimesheet = ts;
    this.rejectReason = '';
    this.rejectError = '';
  }

  closeReject(): void {
    this.rejectingTimesheet = null;
    this.rejectReason = '';
    this.rejectError = '';
  }

  submitReject(): void {
    if (!this.rejectReason.trim()) {
      this.rejectError = 'Please describe the discrepancy.';
      return;
    }
    this.rejectSubmitting = true;
    this.rejectError = '';
    this.http.put<{ success: boolean; timesheet: Timesheet }>(
      `${this.baseUrl}/timesheets/${this.rejectingTimesheet!.id}/reject`,
      { reason: this.rejectReason }
    ).subscribe({
      next: (res) => {
        this.patchTimesheet(res.timesheet);
        this.rejectSubmitting = false;
        this.closeReject();
      },
      error: (err: HttpErrorResponse) => {
        this.rejectError = err.error?.message || 'Something went wrong. Please try again.';
        this.rejectSubmitting = false;
      },
    });
  }

  private patchTimesheet(updated: Timesheet): void {
    if (!this.data) return;
    for (const emp of this.data.employees) {
      const idx = emp.timesheets.findIndex(t => t.id === updated.id);
      if (idx !== -1) { emp.timesheets[idx] = updated; break; }
    }
  }

  canApprove(ts: Timesheet): boolean {
    return ts.status === 'submitted';
  }

  canReject(ts: Timesheet): boolean {
    if (ts.payroll_finalized) return false;
    return ts.status === 'submitted' || ts.status === 'client_approved';
  }
}
